import css from './UserApproval.module.css';
import { useState, useEffect, useContext } from 'react';
import { Registration } from '../../../types/appModels';
import { ErrorCode } from '../../../types/appTypes';
import {
  AdminDataTable,
  BooleanDataColumn,
  DataTableRow,
  DateDataColumn,
  TextDataColumn,
  SortColumn,
  SortDirection,
  DateRangeFilter,
  FilterColumn,
  ActionsColumn,
  TableStatus,
} from '../AdminDataTable/AdminDataTable';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../../context/userContext';
import { useTableData } from '../../../hooks/useTableData';
import { 
  FiCheckCircle,
  FiTrash2
} from 'react-icons/fi';
import NewRegistrationModal from '../NewRegistrationModal/NewRegistrationModal';
import ConfirmationModal from '../../Common/ConfirmationModal/ConfirmationModal';

interface UserApprovalListItem extends DataTableRow {
  name: string;
  regEmail: string;
  createdDate: Date;
  atlasRequest: boolean;
  apiRequest: boolean;
  expired: boolean;
}

const UserApproval = () => {
  const { user } = useContext(UserContext);
  const nav = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showModal, setShowModal] = useState<{
    type: 'delete' | null;
    row: UserApprovalListItem | null;
  }>({ type: null, row: null });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const {
    dataState,
    runSort,
    runFilters,
    loadPage,
    reload,
    startAction,
    actionFailed,
  } = useTableData<Registration>({
    baseEndpoint: `${process.env
      .REACT_APP_API_REGISTRATION_LIST!}?approved=false`,
    initialData: { count: 0, list: [] },
    defaultSort: {
      columnId: 'createdDate',
      direction: SortDirection.Descending,
    },
    defaultSkip: 0,
    defaultLimit: 100,
  });

  const columns = [
    ...(!isMobile ? [
      new TextDataColumn<UserApprovalListItem>('name', 'Name', true, true, 25,
        (value) => (
          <div className={css.textCell}>{value}</div>
        )
      ),
    ] : []),
    new TextDataColumn<UserApprovalListItem>('regEmail', 'Email', true, true, 25,
      (value) => (
        <div className={css.textCell}>{value}</div>
      )
    ),
    new DateDataColumn<UserApprovalListItem>(
      'createdDate',
      'Created',
      true,
      true,
      25,
      (date) => (
        <div className={`${css.dateCell} ${css.hideOnMobile}`}>
          <div>{date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })}</div>
          <div>{date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(',', '')}</div>
        </div>
      ),
      undefined,
      undefined,
      undefined,
      (column, handleFilter) => {
        return <DateRangeFilter column={column} handleFilter={handleFilter} />;
      }
    ),
    new BooleanDataColumn<UserApprovalListItem>(
      'atlasRequest',
      isMobile ? 'Atlas' : 'Atlas Request',
      true,
      true,
      25,
      (value) => (
        <span className={`${css.statusBadge} ${value ? css.statusActive : css.statusInactive}`}>
          {value ? 'Yes' : 'No'}
        </span>
      )
    ),
    new BooleanDataColumn<UserApprovalListItem>(
      'apiRequest',
      isMobile ? 'API' : 'API Request',
      true,
      true,
      25,
      (value) => (
        <span className={`${css.statusBadge} ${value ? css.statusActive : css.statusInactive}`}>
          {value ? 'Yes' : 'No'}
        </span>
      )
    ),
    new BooleanDataColumn<UserApprovalListItem>(
      'expired',
      'Expired',
      false,
      true,
      25,
      (value) => (
        <span className={`${css.statusBadge} ${value ? css.statusInactive : css.statusActive}`}>
          {value ? 'Yes' : 'No'}
        </span>
      )
    ),
    new ActionsColumn<UserApprovalListItem>(
      'actions', 
      'Actions', 
      [
        {
          action: 'APPROVE',
          label: 'approve',
          allowed: (r) => !r.expired,
          icon: <FiCheckCircle />,
        },
        {
          action: 'DELETE',
          label: 'delete',
          icon: <FiTrash2 />,
        },
      ],
      true,
      25
    ),
  ];

  let rows: UserApprovalListItem[] = [];
  let count: number = 0;
  if (dataState.data !== undefined) {
    rows = dataState.data!.list.map<UserApprovalListItem>((item) => {
      return {
        id: item._id!,
        name: item.name,
        regEmail: item.regEmail,
        createdDate: new Date(item.createdDate!),
        expired: item.expired,
        atlasRequest: item.atlasRequest,
        apiRequest: item.apiRequest,
      };
    });

    count = dataState.data!.count;
  }

  const handleSort = (sc: SortColumn | undefined) => {
    runSort(sc);
  };

  const handleFilter = (filters: FilterColumn[]) => {
    runFilters(filters);
  };

  const handleAdd = () => {
    setIsModalOpen(true);
  };

  const handleApproveAction = (r: DataTableRow) => {
    nav(`/approve?id=${r.id}`);
  };

  const handleDeleteAction = async (r: DataTableRow) => {
    setShowModal({ type: 'delete', row: r as UserApprovalListItem });
  };

  const handleConfirmDelete = async () => {
    if (!showModal.row) return;

    startAction();
    fetch(
      `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_REGISTRATION}/${showModal.row.id}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + user!.accessToken,
        },
      }
    )
      .then((response) => {
        if (response.status === 200) {
          reload();
        } else if (response.status === 404) {
          actionFailed(TableStatus.Fail, {
            code: ErrorCode.notFound,
            message:
              'The registration could not be found or has already been deleted.',
          });
        } else {
          actionFailed(TableStatus.Fail, {
            code: ErrorCode.unknown,
            message:
              'An error occurred and the registration could not be deleted.',
          });
        }
      })
      .catch((err) => {
        actionFailed(TableStatus.Error, {
          code: ErrorCode.unknown,
          message:
            'An error occurred and the registration could not be deleted.',
        });
      })
      .finally(() => {
        setShowModal({ type: null, row: null });
      });
  };

  const handleAction = (action: string, row: UserApprovalListItem) => {
    if (action === 'APPROVE') {
      handleApproveAction(row);
    } else if (action === 'DELETE') {
      handleDeleteAction(row);
    }
  };

  // Custom actions renderer
  const renderActionIcons = (row: UserApprovalListItem) => (
    <div className={css.actionsContainer}>
      {!row.expired && (
        <FiCheckCircle 
          className={`${css.actionButton} ${css.approveButton}`} 
          onClick={() => handleApproveAction(row)}
          title="Approve registration"
        />
      )}
      
      <FiTrash2 
        className={`${css.actionButton} ${css.deleteButton}`} 
        onClick={() => handleDeleteAction(row)}
        title="Delete registration"
      />
    </div>
  );

  return (
    <div className={css.userApprovalContainer}>
      <AdminDataTable
        columns={columns}
        rows={rows}
        total={count}
        allowFilter={true}
        allowNoSort={false}
        allowAdd={true}
        addLabel='New Registration'
        onSort={handleSort}
        onFilter={handleFilter}
        onAdd={handleAdd}
        onAction={handleAction}
        state={dataState}
        renderActionIcons={renderActionIcons}
      />
      <NewRegistrationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
      {showModal.type === 'delete' && (
        <ConfirmationModal
          title="Delete Registration"
          message="Are you sure you want to delete this registration?"
          confirmText="Delete"
          icon={<FiTrash2 />}
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowModal({ type: null, row: null })}
        />
      )}
    </div>
  );
};

export default UserApproval;
